import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faDatabase,
  faEye,
  faFilter,
  faSearch,
  faSpinner,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import {
  getBackendLogDetail,
  getBackendLogsFilterOptions,
  listBackendLogs,
} from "../../services/backendLogsApi";

const PER_PAGE_OPTIONS = [10, 20, 50, 100, 200];

const DEFAULT_FILTERS = {
  search_string: "",
  tbl_id: "",
  request_for: "",
  legacy_user: "",
  user_v2: "",
  v2_username: "",
  date_from: "",
  date_to: "",
  sort_by: "tbl_id",
  sort_direction: "desc",
};

const REQUEST_FOR_ACTION_MAP = {
  "#be-be_re": "backend_revert",
  "#be-ap_ch_pr": "ape_change_provider",
  "#be-ap_ch_am": "ape_change_amount",
  "#be-ap_ext": "ape_extension",
  "#be-be_ch_pr": "change_provider",
  "#be-be_ch_mem": "change_member",
  "#be-ph_trn_del": "transfer_physician_delete",
  "#be-hb_pf": "change_payment_setup",
  "#be-tr_loa": "transfer_loa_to_prn",
  "#be-re_loa": "remove_loa_from_prn",
  "#be-ch_soa": "change_soa",
  "#be-2307": "regenerate_form_2307",
  "#be-pis": "regenerate_pis",
  "#be-wht": "wht",
  "#be-rem-pay": "remove_payment_details",
  "#be-change_cov": "change_coverage",
  "#be-gen_sr": "generate_settlement",
  "#be-rem-ds": "remove_deposit_slip",
  "#be-remove_ppd": "remove_ppd",
  "#be-extract_claims": "extract_claims",
};

const DETAIL_FIELDS = [
  ["tbl_id", "Table ID"],
  ["run_datetime", "Run Date/Time"],
  ["request_for", "Request For"],
  ["user", "Legacy User"],
  ["user_v2", "V2 User ID"],
  ["v1_user_id", "V1 User ID"],
  ["v1_username", "V1 Username"],
  ["v1_full_name", "V1 Full Name"],
  ["v2_username", "V2 Username"],
  ["v2_full_name", "V2 Full Name"],
  ["v2_first_name", "V2 First Name"],
  ["v2_last_name", "V2 Last Name"],
  ["v2_email", "V2 Email"],
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-PH");
};

const stringifyValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const formatRequestDataForDisplay = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch (error) {
    return text;
  }
};

const getRequestForAction = (requestFor) => {
  const normalized = String(requestFor ?? "").trim();
  return REQUEST_FOR_ACTION_MAP[normalized] || "unmapped_legacy_action";
};

const getActionBadgeClass = (action) => {
  if (action.startsWith("ape_")) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (["change_provider", "change_member", "transfer_physician_delete"].includes(action)) {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  if (
    [
      "change_payment_setup",
      "transfer_loa_to_prn",
      "remove_loa_from_prn",
      "change_soa",
      "wht",
      "remove_payment_details",
      "generate_settlement",
      "remove_deposit_slip",
      "remove_ppd",
    ].includes(action)
  ) {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }

  if (["regenerate_form_2307", "regenerate_pis"].includes(action)) {
    return "border-cyan-200 bg-cyan-50 text-cyan-700";
  }

  if (action === "extract_claims") {
    return "border-slate-200 bg-slate-50 text-slate-700";
  }

  if (action === "unmapped_legacy_action") {
    return "border-gray-200 bg-gray-50 text-gray-600";
  }

  return "border-blue-200 bg-blue-50 text-blue-700";
};

const ActionBadge = ({ action, className = "" }) => (
  <span
    className={`inline-flex max-w-full rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getActionBadgeClass(
      action
    )} ${className}`}
  >
    <span className="truncate">{action}</span>
  </span>
);

const userBadgeClass = "inline-flex max-w-full rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700";
const legacyBadgeClass = "inline-flex max-w-full rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700";

const getLegacyUserValue = (option) => {
  if (option && typeof option === "object") {
    return String(option.legacy_user ?? option.user ?? "");
  }

  return String(option ?? "");
};

const getLegacyUserBadgeText = (option) => {
  if (!option || typeof option !== "object") {
    return "legacy_user";
  }

  return option.v1_full_name || option.v1_username || `v1_user_id ${option.v1_user_id || ""}`.trim();
};

const getV2UserValue = (user) => String(user?.id ?? "");
const getV2UserPrimaryText = (user) =>
  user?.full_name || user?.username || user?.email || `User #${user?.id || ""}`;

const BadgeFilterDropdown = ({
  label,
  value,
  allLabel,
  options,
  isOpen,
  onToggle,
  onChange,
  getOptionValue,
  getPrimaryText,
  renderBadge,
}) => {
  const selectedOption = (options || []).find(
    (option) => String(getOptionValue(option)) === String(value)
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className="flex min-h-[38px] w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm outline-none transition hover:bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      >
        {selectedOption ? (
          <span className="flex min-w-0 flex-col gap-1">
            <span className="truncate font-medium text-gray-900">
              {getPrimaryText(selectedOption)}
            </span>
            {renderBadge(selectedOption)}
          </span>
        ) : (
          <span className="text-gray-500">{allLabel}</span>
        )}
        <span className="text-xs text-gray-400">{isOpen ? "^" : "v"}</span>
      </button>

      {isOpen && (
        <div className="absolute z-40 mt-1 max-h-72 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={() => onChange("")}
            className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
              value === "" ? "bg-blue-50" : ""
            }`}
          >
            <span className="font-medium text-gray-700">{allLabel}</span>
          </button>
          {(options || []).map((option) => {
            const optionValue = getOptionValue(option);
            const selected = String(optionValue) === String(value);

            return (
              <button
                key={`${label}-${optionValue}`}
                type="button"
                onClick={() => onChange(optionValue)}
                className={`block w-full px-3 py-2 text-left hover:bg-gray-50 ${
                  selected ? "bg-blue-50" : ""
                }`}
              >
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="truncate text-sm font-medium text-gray-900">
                    {getPrimaryText(option)}
                  </span>
                  {renderBadge(option)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const getPaginationWindow = (currentPage, totalPages) => {
  const maxVisible = 5;
  const pages = [];
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, start + maxVisible - 1);

  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let page = start; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
};

const BackendLogsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterForm, setFilterForm] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState({
    request_for: [],
    legacy_user: [],
    v2_users: [],
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [openFilterDropdown, setOpenFilterDropdown] = useState("");

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await getBackendLogsFilterOptions();
      setFilterOptions(response.data?.data || {});
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to load backend log filter options."
      );
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page,
        per_page: perPage,
        ...appliedFilters,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await listBackendLogs(params);
      const payload = response.data || {};
      const pagination = payload.pagination || {};

      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotalRecords(Number(pagination.total_records || 0));
      setTotalPages(Math.max(1, Number(pagination.total_pages || 1)));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Failed to load backend logs."
      );
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, perPage]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const paginationWindow = useMemo(
    () => getPaginationWindow(page, totalPages),
    [page, totalPages]
  );

  const updateFilter = (key, value) => {
    setFilterForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filterForm });
  };

  const handleResetFilters = () => {
    setFilterForm(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
    setPerPage(20);
  };

  const handleSort = (sortByKey) => {
    setPage(1);
    setFilterForm((previous) => {
      const nextDirection =
        previous.sort_by === sortByKey && previous.sort_direction === "asc"
          ? "desc"
          : "asc";
      const nextForm = {
        ...previous,
        sort_by: sortByKey,
        sort_direction: nextDirection,
      };
      setAppliedFilters(nextForm);
      return nextForm;
    });
  };

  const sortLabel = (key) => {
    if (appliedFilters.sort_by !== key) return "";
    return appliedFilters.sort_direction === "asc" ? " ASC" : " DESC";
  };

  const openDetail = async (row) => {
    if (!row?.tbl_id) return;

    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedDetail(null);
    try {
      const response = await getBackendLogDetail(row.tbl_id);
      setSelectedDetail(response.data?.data || null);
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.message ||
          "Failed to load backend log detail."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedDetail(null);
  };

  const renderSortButton = (key, label) => (
    <button
      type="button"
      onClick={() => handleSort(key)}
      className="font-semibold text-gray-700 hover:text-blue-700"
    >
      {label}
      <span className="ml-1 text-[10px] text-gray-400">{sortLabel(key)}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-200 bg-white px-6 py-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-900 text-white">
              <FontAwesomeIcon icon={faDatabase} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Backend Logs</h1>
              <p className="text-sm text-gray-600">
                Review v1 and v2 backend action logs from the `backend_log` table.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchLogs}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faArrowsRotate} />
            Refresh
          </button>
        </div>
      </div>

      <div className="space-y-4 p-6">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {typeof error === "string" ? error : JSON.stringify(error)}
          </div>
        )}

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <form onSubmit={handleApplyFilters} className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <div className="relative md:col-span-2">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={filterForm.search_string}
                onChange={(event) => updateFilter("search_string", event.target.value)}
                placeholder="Search ID, request, data, user..."
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <input
              type="number"
              min="1"
              value={filterForm.tbl_id}
              onChange={(event) => updateFilter("tbl_id", event.target.value)}
              placeholder="Table ID"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />

            <BadgeFilterDropdown
              label="request_for"
              value={filterForm.request_for}
              allLabel="All Request For"
              options={filterOptions.request_for || []}
              isOpen={openFilterDropdown === "request_for"}
              onToggle={() =>
                setOpenFilterDropdown((current) =>
                  current === "request_for" ? "" : "request_for"
                )
              }
              onChange={(nextValue) => {
                updateFilter("request_for", nextValue);
                setOpenFilterDropdown("");
              }}
              getOptionValue={(option) => option}
              getPrimaryText={(option) => option}
              renderBadge={(option) => (
                <ActionBadge action={getRequestForAction(option)} />
              )}
            />

            <BadgeFilterDropdown
              label="legacy_user"
              value={filterForm.legacy_user}
              allLabel="All Legacy Users"
              options={filterOptions.legacy_user || []}
              isOpen={openFilterDropdown === "legacy_user"}
              onToggle={() =>
                setOpenFilterDropdown((current) =>
                  current === "legacy_user" ? "" : "legacy_user"
                )
              }
              onChange={(nextValue) => {
                updateFilter("legacy_user", nextValue);
                setOpenFilterDropdown("");
              }}
              getOptionValue={getLegacyUserValue}
              getPrimaryText={(option) => getLegacyUserValue(option) || "Legacy User"}
              renderBadge={(option) => (
                <span className={legacyBadgeClass}>
                  <span className="truncate">{getLegacyUserBadgeText(option)}</span>
                </span>
              )}
            />

            <BadgeFilterDropdown
              label="v2_user"
              value={filterForm.user_v2}
              allLabel="All V2 Users"
              options={filterOptions.v2_users || []}
              isOpen={openFilterDropdown === "v2_user"}
              onToggle={() =>
                setOpenFilterDropdown((current) =>
                  current === "v2_user" ? "" : "v2_user"
                )
              }
              onChange={(nextValue) => {
                updateFilter("user_v2", nextValue);
                setOpenFilterDropdown("");
              }}
              getOptionValue={getV2UserValue}
              getPrimaryText={getV2UserPrimaryText}
              renderBadge={(user) => (
                <span className={userBadgeClass}>
                  <span className="truncate">
                    {user?.username || user?.email || "oauth_user"} #{user?.id}
                  </span>
                </span>
              )}
            />

            <input
              type="date"
              value={filterForm.date_from}
              onChange={(event) => updateFilter("date_from", event.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />

            <input
              type="date"
              value={filterForm.date_to}
              onChange={(event) => updateFilter("date_to", event.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />

            <div className="flex flex-wrap items-center gap-2 md:col-span-4">
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
              >
                <FontAwesomeIcon icon={faFilter} />
                Apply Filters
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 text-sm text-gray-600">
            <span>{showingText}</span>
            <div className="flex items-center gap-2">
              <span>Display per page</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 px-2 py-1"
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-auto">
            <table className="min-w-[1500px] text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-3 text-left">{renderSortButton("tbl_id", "tbl_id")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("run_datetime", "run_datetime")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("request_for", "request_for")}</th>
                  <th className="px-3 py-3 text-left">request_data</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("user", "user")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("user_v2", "user_v2")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("v1_username", "v1_username")}</th>
                  <th className="px-3 py-3 text-left">v1_full_name</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("v2_username", "v2_username")}</th>
                  <th className="px-3 py-3 text-left">{renderSortButton("v2_full_name", "v2_full_name")}</th>
                  <th className="px-3 py-3 text-left">v2_email</th>
                  <th className="sticky right-0 bg-gray-50 px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-10 text-center text-gray-500">
                      <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                      Loading backend logs...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-4 py-10 text-center text-gray-500">
                      No backend logs found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.tbl_id} className="hover:bg-blue-50/30">
                      <td className="px-3 py-3 font-mono text-xs">{row.tbl_id}</td>
                      <td className="px-3 py-3">{formatDateTime(row.run_datetime)}</td>
                      <td className="px-3 py-3">
                        <div className="space-y-1.5">
                          <div className="font-mono text-xs text-gray-900">
                            {row.request_for || "-"}
                          </div>
                          <span
                            className={`inline-flex max-w-[220px] rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getActionBadgeClass(
                              getRequestForAction(row.request_for)
                            )}`}
                          >
                            <span className="truncate">
                              {getRequestForAction(row.request_for)}
                            </span>
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <textarea
                          readOnly
                          value={formatRequestDataForDisplay(row.request_data)}
                          title={row.request_data || ""}
                          className="h-20 w-[320px] resize-y rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-[11px] leading-4 text-gray-700 outline-none"
                        />
                      </td>
                      <td className="px-3 py-3 font-mono text-xs">{row.user || "-"}</td>
                      <td className="px-3 py-3 font-mono text-xs">{row.user_v2 ?? "-"}</td>
                      <td className="px-3 py-3">{row.v1_username || "-"}</td>
                      <td className="px-3 py-3">{row.v1_full_name || "-"}</td>
                      <td className="px-3 py-3">{row.v2_username || "-"}</td>
                      <td className="px-3 py-3">{row.v2_full_name || "-"}</td>
                      <td className="px-3 py-3">{row.v2_email || "-"}</td>
                      <td className="sticky right-0 bg-white px-3 py-3">
                        <button
                          type="button"
                          onClick={() => openDetail(row)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
            <div className="text-sm text-gray-600">Page {page} of {totalPages}</div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                disabled={page <= 1}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Prev
              </button>

              {paginationWindow.map((windowPage) => (
                <button
                  key={windowPage}
                  type="button"
                  onClick={() => setPage(windowPage)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                    windowPage === page
                      ? "bg-gray-900 text-white"
                      : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {windowPage}
                </button>
              ))}

              <button
                type="button"
                onClick={() => setPage((previous) => Math.min(totalPages, previous + 1))}
                disabled={page >= totalPages}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {detailOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-md border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Backend Log Detail</h3>
                <p className="text-xs text-gray-500">
                  tbl_id {selectedDetail?.tbl_id || "Loading..."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-md border border-gray-300 px-2.5 py-1.5 text-gray-500 hover:bg-gray-50"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="max-h-[78vh] space-y-4 overflow-auto p-5">
              {detailLoading ? (
                <div className="py-14 text-center text-gray-500">
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Loading detail...
                </div>
              ) : !selectedDetail ? (
                <div className="py-14 text-center text-gray-500">
                  No detail available.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {DETAIL_FIELDS.map(([key, label]) => (
                      <div key={key} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                        <p className="text-xs font-semibold text-gray-500">{label}</p>
                        <p className="mt-1 break-words text-sm text-gray-900">
                          {key === "run_datetime"
                            ? formatDateTime(selectedDetail[key])
                            : selectedDetail[key] ?? "-"}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-md border border-gray-200 p-4">
                    <h4 className="mb-2 text-sm font-semibold text-gray-900">request_data</h4>
                    <pre className="max-h-[360px] overflow-auto rounded-md bg-gray-900 p-3 text-xs leading-5 text-gray-100">
                      {stringifyValue(selectedDetail.request_data_json || selectedDetail.request_data)}
                    </pre>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackendLogsPage;
